import {Component, HostListener} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {ProgressSpinner} from 'primeng/progressspinner';
import {Tooltip} from 'primeng/tooltip';
import {NavigationEnd, Router} from '@angular/router';
import {combineLatest, Observable} from 'rxjs';
import {filter, map, startWith} from 'rxjs/operators';
import {CatastoJobService} from '../service/kleros/catasto-job.service';
import {SalvataggioImmobiliJobService} from '../service/kleros/salvataggio-immobili-job.service';
import {RouteParamsService} from '../service';
import {StorageService} from '../service/utils/storage.service';
import {JobCatastoModel, JobSalvataggioImmobiliModel, StatoJobCatasto, TabPatrimonioKey} from '../../shared/models';
import {PATRIMONIO_TAB} from '../../shared/constants/app-constants';

// frammento della rotta del patrimonio: serve solo a capire se ci siamo già, per non navigare inutilmente
const ROTTA_PATRIMONIO = '/funzionalita/patrimonio';

// rotte che stanno "dentro" un dossier: fuori da queste (lista dossier, profilo, wallet) l'indicatore si nasconde
const ROTTE_DOSSIER = ['/funzionalita/', '/documenti'];

/**
 * Indicatore fluttuante dei lavori in background (elaborazione visura e salvataggio immobili), visibile solo
 * dentro il dossier a cui appartengono. Quello del catasto, a fine elaborazione, apre il raggruppamento.
 */
@Component({
	selector: 'catasto-job-indicator',
	imports: [AsyncPipe, TranslatePipe, ProgressSpinner, Tooltip],
	template: `
    <div class="lavori-container">
      @if (jobCatasto$ | async; as job) {
        <div class="lavoro-card shadow-4 border-round-lg surface-0 p-3"
             [class.lavoro-card-clickable]="job.stato === StatoJob.COMPLETATO"
             [class.border-green-500]="job.stato === StatoJob.COMPLETATO"
             [class.border-yellow-500]="job.stato === StatoJob.ERRORE"
             [class.border-primary]="job.stato === StatoJob.IN_CORSO"
             (click)="job.stato === StatoJob.COMPLETATO ? apriRaggruppamento() : null">
          <div class="flex align-items-center gap-3">
            @switch (job.stato) {
              @case (StatoJob.IN_CORSO) {
                <p-progressSpinner strokeWidth="4" styleClass="w-2rem h-2rem"/>
                <div class="flex flex-column">
                  <span class="font-semibold text-sm">{{ 'catastoWizard.elaborazioneInCorso'|translate }}</span>
                  <span class="text-xs text-color-secondary">{{ 'catastoWizard.puoiContinuareALavorare'|translate }}</span>
                  <span class="text-xs text-orange-600 font-medium mt-1">
                    <i class="mdi mdi-alert-outline"></i> {{ 'catastoWizard.nonAggiornareLaPagina'|translate }}
                  </span>
                </div>
              }
              @case (StatoJob.COMPLETATO) {
                <i class="mdi mdi-check-circle text-green-500 text-3xl"></i>
                <div class="flex flex-column">
                  <span class="font-semibold text-sm">{{ 'catastoWizard.visuraElaborata'|translate }}</span>
                  <span class="text-xs text-primary font-medium">
                    {{ 'catastoWizard.apriRaggruppamento'|translate }} <i class="mdi mdi-arrow-right"></i>
                  </span>
                </div>
              }
              @case (StatoJob.ERRORE) {
                <i class="mdi mdi-alert-circle text-yellow-600 text-3xl"></i>
                <div class="flex flex-column" style="max-width: 16rem">
                  <span class="font-semibold text-sm">{{ 'catastoWizard.visuraNonElaborata'|translate }}</span>
                  <span class="text-xs text-color-secondary white-space-normal">{{ catastoJobService.messaggioErrore(job) }}</span>
                </div>
              }
            }
            <!-- niente chiusura mentre il lavoro è in corso: l'indicatore è l'unico segnale che sta girando -->
            @if (job.stato !== StatoJob.IN_CORSO) {
              <i class="mdi mdi-close text-color-secondary cursor-pointer ml-2"
                 [pTooltip]="'chiudi'|translate" tooltipPosition="left"
                 (click)="catastoJobService.pulisci(); $event.stopPropagation()">
              </i>
            }
          </div>
        </div>
      }

      @if (jobSalvataggio$ | async; as job) {
        <div class="lavoro-card shadow-4 border-round-lg surface-0 p-3"
             [class.border-green-500]="job.stato === StatoJob.COMPLETATO"
             [class.border-yellow-500]="job.stato === StatoJob.ERRORE"
             [class.border-primary]="job.stato === StatoJob.IN_CORSO">
          <div class="flex align-items-center gap-3">
            @switch (job.stato) {
              @case (StatoJob.IN_CORSO) {
                <p-progressSpinner strokeWidth="4" styleClass="w-2rem h-2rem"/>
                <div class="flex flex-column">
                  <span class="font-semibold text-sm">
                    {{ 'catastoWizard.salvataggioInCorso'|translate:{numero: job.totaleImmobili} }}
                  </span>
                  <span class="text-xs text-color-secondary">{{ 'catastoWizard.puoiContinuareALavorare'|translate }}</span>
                  <span class="text-xs text-orange-600 font-medium mt-1">
                    <i class="mdi mdi-alert-outline"></i> {{ 'catastoWizard.nonAggiornareLaPagina'|translate }}
                  </span>
                </div>
              }
              @case (StatoJob.COMPLETATO) {
                <i class="mdi mdi-check-circle text-green-500 text-3xl"></i>
                <span class="font-semibold text-sm">{{ 'catastoWizard.salvataggioCompletato'|translate }}</span>
              }
              @case (StatoJob.ERRORE) {
                <i class="mdi mdi-alert-circle text-yellow-600 text-3xl"></i>
                <div class="flex flex-column" style="max-width: 16rem">
                  <span class="font-semibold text-sm">{{ 'catastoWizard.salvataggioNonRiuscito'|translate }}</span>
                  <span class="text-xs text-color-secondary white-space-normal">{{ job.errore }}</span>
                </div>
              }
            }
            @if (job.stato !== StatoJob.IN_CORSO) {
              <i class="mdi mdi-close text-color-secondary cursor-pointer ml-2"
                 [pTooltip]="'chiudi'|translate" tooltipPosition="left"
                 (click)="salvataggioJobService.pulisci()">
              </i>
            }
          </div>
        </div>
      }
    </div>
	`,
	styles: `
    .lavori-container {
      position: fixed;
      /* sopra l'icona del chatbot: 4rem di icona + i suoi 10px dal fondo + un margine */
      bottom: calc(4rem + 20px);
      right: 10px;
      z-index: 1001;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: .5rem;
      /* il contenitore non deve intercettare i click quando è vuoto: li riattivano le singole card */
      pointer-events: none;
    }

    .lavoro-card {
      pointer-events: auto;
      border-left: 4px solid;
      max-width: 22rem;
      animation: lavoro-card-in .25s ease-out;
    }

    .lavoro-card-clickable {
      cursor: pointer;
      transition: transform .15s ease, box-shadow .15s ease;
    }

    .lavoro-card-clickable:hover {
      transform: translateY(-2px);
    }

    @keyframes lavoro-card-in {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
	`
})
export class CatastoJobIndicatorComponent {

	protected readonly StatoJob = StatoJobCatasto;

	// vero quando la pagina corrente sta dentro un dossier
	private readonly dentroDossier$: Observable<boolean> = this.router.events.pipe(
		filter(evento => evento instanceof NavigationEnd),
		map(() => ROTTE_DOSSIER.some(rotta => this.router.url.includes(rotta))),
		startWith(ROTTE_DOSSIER.some(rotta => this.router.url.includes(rotta)))
	);

	// visibili solo dentro il dossier di appartenenza; il monitoraggio però non si ferma mai
	protected readonly jobCatasto$: Observable<JobCatastoModel | null> = combineLatest([
		this.catastoJobService.jobDossierCorrente$,
		this.dentroDossier$
	]).pipe(map(([job, dentroDossier]) => dentroDossier ? job : null));

	protected readonly jobSalvataggio$: Observable<JobSalvataggioImmobiliModel | null> = combineLatest([
		this.salvataggioJobService.jobDossierCorrente$,
		this.dentroDossier$
	]).pipe(map(([job, dentroDossier]) => dentroDossier ? job : null));

	constructor(protected catastoJobService: CatastoJobService,
	            protected salvataggioJobService: SalvataggioImmobiliJobService,
	            private router: Router,
	            private routeService: RouteParamsService,
	            private storage: StorageService) {
	}

	/**
	 * Il jobId vive solo in memoria: ricaricando si perde il modo di recuperare il risultato. Si guardano gli
	 * stati grezzi e non quelli filtrati per dossier, perché il danno c'è da qualunque pagina. Il testo del
	 * dialog lo decide il browser, basta annullare l'evento.
	 */
	@HostListener('window:beforeunload', ['$event'])
	avvisaSeLavoriInCorso(evento: BeforeUnloadEvent): void {
		if (this.catastoJobService.inCorso || this.salvataggioJobService.inCorso) {
			evento.preventDefault();
			evento.returnValue = '';
		}
	}

	/**
	 * Porta l'utente al raggruppamento, da qualunque pagina si trovi.
	 *
	 * Il raggruppamento si apre dal patrimonio immobiliare, che è l'unico a sapere dove salvare gli immobili.
	 * Il tab da mostrare viene indicato tramite la chiave di sessione che PatrimonioComponent rilegge in fase di
	 * inizializzazione, mentre la richiesta di apertura resta in sospeso nel service finché il dossier
	 * immobiliare non viene creato e la raccoglie: i tab sono in lazy, quindi prima di allora non esiste.
	 */
	protected apriRaggruppamento(): void {
		this.storage.setItemSession(PATRIMONIO_TAB, TabPatrimonioKey.IMMOBILIARE);
		this.catastoJobService.richiediApertura();

		if (!this.router.url.includes(ROTTA_PATRIMONIO)) {
			this.router.navigate(['/applicazioni', this.routeService.params['app'], 'funzionalita', 'patrimonio']);
		}
	}
}
